import React, { useState } from 'react';
import { Reorder, useDragControls, motion } from 'framer-motion';
import { GripVertical, X, Plus, ChevronDown, ArrowLeft, Settings } from 'lucide-react';
interface ListItem {
  id: string;
  name: string;
  display: 'Condensed' | 'Full';
}
interface SummaryListResult {
  person: string;
  lines: string[];
  time: string;
  meta?: string;
}
interface SummarySection {
  id: string;
  name: string;
  display: 'Condensed' | 'Full';
  results: SummaryListResult[];
}
interface Summary {
  id: string;
  title: string;
  lists: ListItem[];
  sections: SummarySection[];
}
const SUMMARIES: Summary[] = [{
  id: '1',
  title: 'Morning Briefing',
  lists: [{
    id: '1',
    name: 'Priority emails this week',
    display: 'Condensed'
  }, {
    id: '2',
    name: "Today's meetings",
    display: 'Full'
  }, {
    id: '3',
    name: 'Recent Jira updates',
    display: 'Condensed'
  }, {
    id: '4',
    name: 'Transactions this week',
    display: 'Condensed'
  }],
  sections: [{
    id: '1',
    name: 'Priority emails this week',
    display: 'Condensed',
    results: [{
      person: 'Sarah',
      lines: ['needs Q2 budget approval (Friday)'],
      time: 'this morning'
    }, {
      person: 'James, Acme Legal',
      lines: ['wants section 4.2 liability cap adjusted', 'sent DPA review comments', 'wants a 15-min call (Friday)'],
      time: 'this morning'
    }, {
      person: 'Lisa, DevOps',
      lines: ['needs SSL renewal PR approved (Friday)'],
      time: 'yesterday'
    }]
  }, {
    id: '2',
    name: "Today's meetings",
    display: 'Full',
    results: [{
      person: 'Team standup',
      lines: ['daily engineering sync'],
      time: '9:00 AM',
      meta: '15 min'
    }, {
      person: 'Sarah + James',
      lines: ['Q2 budget review'],
      time: '11:00 AM',
      meta: '45 min'
    }, {
      person: 'Acme Corp',
      lines: ['partnership call (review section 4.2)'],
      time: '2:00 PM',
      meta: '30 min'
    }]
  }, {
    id: '3',
    name: 'Recent Jira updates',
    display: 'Condensed',
    results: [{
      person: 'Bug',
      lines: ['rate limiting for /sync'],
      time: 'Zen Bot'
    }, {
      person: 'Task',
      lines: ['SSL cert renewal automation'],
      time: 'Zen Bot'
    }]
  }, {
    id: '4',
    name: 'Transactions this week',
    display: 'Condensed',
    results: [{
      person: 'Hetzner',
      lines: ['server hosting'],
      time: 'today',
      meta: '\u00a347.20'
    }, {
      person: 'OpenAI',
      lines: ['API usage'],
      time: 'Monday',
      meta: '\u00a3156.80'
    }]
  }]
}, {
  id: '2',
  title: 'Finance Summary',
  lists: [{
    id: '5',
    name: 'Transactions this week',
    display: 'Full'
  }, {
    id: '6',
    name: 'Monthly spend by category',
    display: 'Full'
  }, {
    id: '7',
    name: 'Outstanding invoices',
    display: 'Condensed'
  }],
  sections: [{
    id: '5',
    name: 'Transactions this week',
    display: 'Full',
    results: [{
      person: 'Hetzner',
      lines: ['server hosting'],
      time: 'today',
      meta: '\u00a347.20'
    }, {
      person: 'Vercel',
      lines: ['Pro plan'],
      time: 'yesterday',
      meta: '\u00a320.00'
    }, {
      person: 'OpenAI',
      lines: ['API usage'],
      time: 'Monday',
      meta: '\u00a3156.80'
    }]
  }, {
    id: '6',
    name: 'Monthly spend by category',
    display: 'Full',
    results: [{
      person: 'Infrastructure',
      lines: ['Hetzner, Vercel, Cloudflare'],
      time: 'this month',
      meta: '\u00a3312.40'
    }, {
      person: 'AI / APIs',
      lines: ['OpenAI, Anthropic, LiveKit'],
      time: 'this month',
      meta: '\u00a3489.60'
    }]
  }, {
    id: '7',
    name: 'Outstanding invoices',
    display: 'Condensed',
    results: [{
      person: 'Acme Corp',
      lines: ['March invoice'],
      time: 'due Friday',
      meta: '\u00a32,400'
    }]
  }]
}, {
  id: '3',
  title: 'Contacts Context',
  lists: [{
    id: '8',
    name: 'Top contacts by interaction',
    display: 'Full'
  }, {
    id: '9',
    name: 'Unresolved email threads',
    display: 'Condensed'
  }],
  sections: [{
    id: '8',
    name: 'Top contacts by interaction',
    display: 'Full',
    results: [{
      person: 'Sarah',
      lines: ['24 interactions'],
      time: 'this month'
    }, {
      person: 'Tom',
      lines: ['18 interactions'],
      time: 'this month'
    }, {
      person: 'James, Acme Legal',
      lines: ['12 interactions'],
      time: 'this month'
    }]
  }, {
    id: '9',
    name: 'Unresolved email threads',
    display: 'Condensed',
    results: [{
      person: 'Paul, Partnerships',
      lines: ['needs privacy policy URL'],
      time: 'this morning'
    }, {
      person: 'Acme Legal',
      lines: ['sent DPA review comments'],
      time: 'yesterday'
    }]
  }]
}, {
  id: '4',
  title: 'Weekly Review',
  lists: [{
    id: '10',
    name: 'Completed Jira issues',
    display: 'Condensed'
  }, {
    id: '11',
    name: 'Sent emails this week',
    display: 'Condensed'
  }, {
    id: '13',
    name: 'Weekly spend summary',
    display: 'Full'
  }],
  sections: [{
    id: '10',
    name: 'Completed Jira issues',
    display: 'Condensed',
    results: [{
      person: 'Story',
      lines: ['Nango webhook pipeline'],
      time: 'Zen Bot'
    }, {
      person: 'Bug',
      lines: ['fix 429 retry logic'],
      time: 'Zen Bot'
    }]
  }, {
    id: '13',
    name: 'Weekly spend summary',
    display: 'Full',
    results: [{
      person: 'Total',
      lines: ['all categories'],
      time: 'this week',
      meta: '\u00a3299.00'
    }]
  }]
}];
export function SummaryConfig() {
  const [viewingId, setViewingId] = useState<string | null>(null);
  if (viewingId) {
    const summary = SUMMARIES.find(s => s.id === viewingId);
    if (summary) return <SummaryDetail summary={summary} onBack={() => setViewingId(null)} data-id="element-1780" />;
  }
  return <SummaryList onSelect={id => setViewingId(id)} data-id="element-1781" />;
}
function SummaryList({
  onSelect
}: {
  onSelect: (id: string) => void;
}) {
  return <div className="min-h-[calc(100vh-56px)] pt-12 pb-32 px-6" data-id="element-1782">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1783">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-8" data-id="element-1784">Summaries</h1>
        <div className="flex flex-col gap-3" data-id="element-1785">
          {SUMMARIES.map((summary, i) => <motion.button key={summary.id} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          delay: i * 0.04
        }} onClick={() => onSelect(summary.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left text-[15px] text-[#e8e4df] hover:bg-[#1e1e25] transition-colors" data-id="element-1786">
              {summary.title}
            </motion.button>)}
        </div>
      </div>
    </div>;
}
function SummaryDetail({
  summary,
  onBack
}: {
  summary: Summary;
  onBack: () => void;
}) {
  const [showConfig, setShowConfig] = useState(false);
  if (showConfig) return <SummaryEditor summary={summary} onBack={() => setShowConfig(false)} data-id="element-1787" />;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1788">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1789">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-1790"><ArrowLeft size={14} data-id="element-1791" /><span className="text-[13px]" data-id="element-1792">Summaries</span></button>
        <header className="mb-12 flex items-start justify-between" data-id="element-1793">
          <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight" data-id="element-1794">{summary.title}</h1>
          <button onClick={() => setShowConfig(true)} className="text-[#8a8680]/30 hover:text-[#8a8680] transition-colors" title="Configure" data-id="element-1795"><Settings size={15} data-id="element-1796" /></button>
        </header>

        <div className="flex flex-col gap-12" data-id="element-1797">
          {summary.sections.map((section, sIndex) => <motion.div key={section.id} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4,
          delay: sIndex * 0.08
        }} data-id="element-1798">
              <h2 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-1799">{section.name}</h2>
              <div className="flex flex-col gap-3" data-id="element-1800">
                {section.results.map((item, index) => <div key={index} className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-1801">
                    <p className="font-semibold text-[#e8e4df] text-[15px] leading-[1.5]" data-id="element-1802">{item.person}</p>
                    <div className="mt-2 space-y-1" data-id="element-1803">
                      {item.lines.map((line, j) => <p key={j} className="text-[#e8e4df]/75 text-[14px] leading-[1.55]" data-id="element-1804">{line}</p>)}
                    </div>
                    <p className="text-[12px] text-[#8a8680]/50 mt-3" data-id="element-1805">
                      {item.time}{item.meta && <span data-id="element-1806"> \u00b7 {item.meta}</span>}
                    </p>
                  </div>)}
              </div>
            </motion.div>)}
        </div>
      </div>
    </motion.div>;
}
function SummaryEditor({
  summary,
  onBack
}: {
  summary: Summary;
  onBack: () => void;
}) {
  const [title, setTitle] = useState(summary.title);
  const [lists, setLists] = useState(summary.lists);
  const handleRemove = (id: string) => setLists(lists.filter(l => l.id !== id));
  const handleDisplayChange = (id: string, display: 'Condensed' | 'Full') => {
    setLists(lists.map(l => l.id === id ? {
      ...l,
      display
    } : l));
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] w-full flex items-center justify-center p-6 text-[#e8e4df]" data-id="element-1807">
      <div className="w-full max-w-[640px] flex flex-col gap-8" data-id="element-1808">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1" data-id="element-1809"><ArrowLeft size={14} data-id="element-1810" /><span className="text-[13px]" data-id="element-1811">Back</span></button>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-transparent text-[20px] font-medium text-[#e8e4df] outline-none" data-id="element-1812" />
        <Reorder.Group axis="y" values={lists} onReorder={setLists} className="flex flex-col" data-id="element-1813">
          {lists.map(item => <ReorderItem key={item.id} item={item} onRemove={() => handleRemove(item.id)} onDisplayChange={val => handleDisplayChange(item.id, val)} data-id="element-1814" />)}
        </Reorder.Group>
        <button className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors self-start" data-id="element-1815"><Plus size={13} data-id="element-1816" /><span className="text-[13px]" data-id="element-1817">Add list</span></button>
        <div className="flex items-center justify-center gap-6 mt-4" data-id="element-1818">
          <button className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] transition-colors" data-id="element-1819">Save</button>
          <button className="text-[14px] text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors" data-id="element-1820">Reset</button>
        </div>
      </div>
    </motion.div>;
}
function ReorderItem({
  item,
  onRemove,
  onDisplayChange
}: {
  item: ListItem;
  onRemove: () => void;
  onDisplayChange: (val: 'Condensed' | 'Full') => void;
}) {
  const dragControls = useDragControls();
  return <Reorder.Item value={item} id={item.id} dragListener={false} dragControls={dragControls} className="flex items-center gap-3 py-3 group" data-id="element-1821">
      <div className="cursor-grab active:cursor-grabbing text-[#8a8680]/30 hover:text-[#8a8680]/60 transition-colors" onPointerDown={e => dragControls.start(e)} data-id="element-1822"><GripVertical size={14} data-id="element-1823" /></div>
      <span className="flex-1 text-[#e8e4df]/80 text-[14px] truncate" data-id="element-1824">{item.name}</span>
      <select value={item.display} onChange={e => onDisplayChange(e.target.value as 'Condensed' | 'Full')} className="bg-transparent text-[#8a8680]/60 text-[12px] outline-none cursor-pointer appearance-none pr-4" data-id="element-1825"><option value="Condensed" data-id="element-1826">Condensed</option><option value="Full" data-id="element-1827">Full</option></select>
      <button onClick={onRemove} className="text-[#8a8680]/0 group-hover:text-[#8a8680]/40 transition-opacity" data-id="element-1828"><X size={13} data-id="element-1829" /></button>
    </Reorder.Item>;
}